import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { SendSMSRequest, FormErrors } from '../types';
import { apiService } from '../services/api';

interface SendFormState {
  // Form data
  phoneNumber: string;
  message: string;
  
  // Form state
  isLoading: boolean;
  errors: FormErrors;
  
  // Result state
  lastResult: {
    success: boolean;
    message: string;
  } | null;
  
  // Actions
  setPhoneNumber: (phone: string) => void;
  setMessage: (message: string) => void;
  setErrors: (errors: FormErrors) => void;
  clearErrors: () => void;
  clearResult: () => void;
  validateForm: () => boolean;
  sendSMS: () => Promise<void>;
  resetForm: () => void;
}

const MAX_MESSAGE_LENGTH = 160;

const validatePhoneNumber = (phone: string): boolean => {
  const phoneRegex = /^\+[1-9]\d{10,14}$/;
  return phoneRegex.test(phone);
};

const initialState = {
  phoneNumber: '',
  message: '',
  isLoading: false,
  errors: {},
  lastResult: null,
};

export const useSendFormStore = create<SendFormState>()(
  devtools(
    (set, get) => ({
      ...initialState,

      setPhoneNumber: (phone: string) => {
        set({ phoneNumber: phone });
        
        // Clear phone error if validation passes
        const { errors } = get();
        if (errors.phone && validatePhoneNumber(phone)) {
          set({
            errors: { ...errors, phone: '' }
          });
        }
      },

      setMessage: (message: string) => {
        // Enforce character limit
        if (message.length <= MAX_MESSAGE_LENGTH) {
          set({ message });
          
          // Clear message error if validation passes
          const { errors } = get();
          if (errors.message && message.trim().length > 0) {
            set({
              errors: { ...errors, message: '' }
            });
          }
        }
      },

      setErrors: (errors: FormErrors) => {
        set({ errors });
      },

      clearErrors: () => {
        set({ errors: {} });
      },

      clearResult: () => {
        set({ lastResult: null });
      },

      validateForm: (): boolean => {
        const { phoneNumber, message } = get();
        const newErrors: FormErrors = {};

        // Validate phone number
        if (!phoneNumber.trim()) {
          newErrors.phone = 'Phone number is required';
        } else if (!validatePhoneNumber(phoneNumber)) {
          newErrors.phone = 'Please enter a valid phone number (e.g., +1234567890)';
        }

        // Validate message
        if (!message.trim()) {
          newErrors.message = 'Message is required';
        }

        set({ errors: newErrors });
        return Object.keys(newErrors).length === 0;
      },

      sendSMS: async () => {
        const { phoneNumber, message, validateForm } = get();
        
        // Clear previous result
        set({ lastResult: null });
        
        // Validate form
        if (!validateForm()) {
          return;
        }

        set({ isLoading: true });

        try {
          const request: SendSMSRequest = {
            to: phoneNumber,
            body: message,
          };

          const response = await apiService.sendSMS(request);

          if (response.success) {
            set({
              lastResult: {
                success: true,
                message: `SMS sent successfully to ${phoneNumber}`,
              },
              isLoading: false,
            });

            // Reset form on success
            get().resetForm();
          } else {
            // Handle validation errors from backend
            if (response.errors) {
              const backendErrors: FormErrors = {};
              
              if (response.errors.to) {
                backendErrors.phone = response.errors.to[0];
              }
              if (response.errors.body) {
                backendErrors.message = response.errors.body[0];
              }
              
              set({ errors: backendErrors });
            }

            set({
              lastResult: {
                success: false,
                message: response.error || 'Failed to send SMS',
              },
              isLoading: false,
            });
          }
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to send SMS';
          
          set({
            lastResult: {
              success: false,
              message: errorMessage,
            },
            isLoading: false,
          });
        }
      },

      resetForm: () => {
        set({
          phoneNumber: '',
          message: '',
          errors: {},
        });
      },
    }),
    {
      name: 'send-form-store',
    }
  )
);

// Utility functions for form validation
export const getCharacterCount = (message: string): number => message.length;
export const getRemainingCharacters = (message: string): number => MAX_MESSAGE_LENGTH - message.length;
export const isCharacterLimitReached = (message: string): boolean => message.length >= MAX_MESSAGE_LENGTH;
export const isCharacterWarning = (message: string): boolean => getRemainingCharacters(message) < 20;